import React, { Fragment } from 'react'
import { capitalize } from 'lodash'
import { Redirect } from 'react-router-dom'
import { Step, Stepper, StepButton } from 'material-ui-legacy/Stepper'
import { red500 } from 'material-ui-legacy/styles/colors'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'

// Icons
import ActionDelete from 'material-ui-legacy/svg-icons/action/delete'
//import ActionTransfer from 'material-ui-legacy/svg-icons/action/transfer'

// Custom UI components
import errors from '../../lib/errors.js'
import EditButton from '../UI/EditButton'
import CloneButton from '../UI/CloneButton'
import DeleteButton from '../UI/DeleteButton'
import PrintButton from '../UI/PrintButton'
import ErrorDialog from '../UI/ErrorDialog'
import ConfirmationDialog from '../UI/ConfirmationDialog'
import SaveButton from '../UI/SaveButton'
import CancelButton from '../UI/CancelButton'

import AutoCompleteSelect from '../UI/AutoCompleteSelect'
import Dialog from 'material-ui-legacy/Dialog'

import ListQuotes from './ListQuotes'
import Resource from '../../stores/resource'

const QuoteStore = Resource('quotes')
const ProjectStore = Resource('projects')

// INTERNAL COMPONENTS ---------------------------------------------------------

/*
Component used to display quote action buttons within details row on DataGrid
Props:
- editCallback,
  cloneCallback,
  printCallback,
  deleteCallback: action callbacks
- readOnly: Boolean(false) self-explanatory
*/
const QuoteActionButtons = ({
  editCallback,
  cloneCallback,
  cloneActualCallback,
  printCallback,
  copyCallback,
  deleteCallback,
  readOnly
}) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ marginRight: '10px' }}>Actions:</span>
    {!readOnly && editCallback && <EditButton onTouchTap={editCallback} />}
    {!readOnly && cloneCallback && <CloneButton onTouchTap={cloneCallback} />}
    {!readOnly &&
      cloneActualCallback && (
        <CloneButton label="Clone actual" onTouchTap={cloneActualCallback} />
      )}
    {printCallback && <PrintButton onTouchTap={printCallback} />}
    {copyCallback && <CloneButton label="Copy to Another Project" onTouchTap={copyCallback} />}
    {!readOnly &&
      deleteCallback && <DeleteButton onTouchTap={deleteCallback} />}
  </div>
)

/*
State management component for quotes, displayed within details row
Props:
- draftCallback,
  pendingCallback,
  awardedCallback,
  actualCallback: callbacks to handle state changes
- readOnly: Boolean(false) self-explanatory
*/
const QuoteState = ({
  quoteRow,
  readOnly,
  draftCallback,
  pendingCallback,
  awardedCallback,
  actualCallback
}) => {
  const stateIndexes = {
    draft: 0,
    pending: 1,
    awarded: 2,
    actual: 3
  }
  const currentStep = quoteRow.state
  const stepIndex = stateIndexes[currentStep]

  const stepperStyle = {
    marginLeft: '20px',
    display: 'flex',
    alignItems: 'center'
  }

  const shouldDisable = step => {
    // Don't allow step change in readOnly mode
    if (readOnly && step === stepIndex) return false
    if (readOnly && step !== stepIndex) return true
    // Don't allow backtracking from Actual
    if (stepIndex === 3 && step < 3) return true

    // NOTE: This prevents skipping steps, but we'll allow it
    //       for a while so the people at GM can update quotes
    //       to the current state of things.
    // return step > stepIndex + 1 || step < stepIndex - 1
    return false
  }

  const Arrow = () => <span>&rarr;</span>

  return (
    <div style={stepperStyle}>
      <span>State:</span>
      <Stepper linear={false} activeStep={stepIndex} connector={<Arrow />}>
        <Step disabled={shouldDisable(0)}>
          <StepButton onClick={draftCallback}>Draft</StepButton>
        </Step>

        <Step disabled={shouldDisable(1)}>
          <StepButton onClick={pendingCallback}>Pending</StepButton>
        </Step>

        <Step disabled={shouldDisable(2)}>
          <StepButton onClick={awardedCallback}>Awarded</StepButton>
        </Step>

        <Step disabled={shouldDisable(3)}>
          <StepButton onClick={actualCallback}>Actual</StepButton>
        </Step>
      </Stepper>
    </div>
  )
}

/*
Component used to display details row on DataGrid
Props:
- quoteRow: a quote row from ListQuotes (NOT a quote object!)

- editCallback,
  cloneCallback,
  printCallback,
  deleteCallback: passed down to QuoteActionButtons

- draftCallback,
  pendingCallback,
  awardedCallback,
  actualCallback: passed down to QuoteState

- readOnly: Boolean(false) self-explanatory
*/
const QuoteDetailsRow = props => (
  <section style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
    <QuoteActionButtons {...props} />
    <QuoteState {...props} />
  </section>
)

const QuoteCopyDialog = ({
  changeCallback,
  copyCallback,
  cancelCallback,
  project,
  open,
  quote
}) => {

  const actions = [
    <SaveButton
      disabled={ project == null }
      onTouchTap={ () => (project != null) && copyCallback(project) }
      label={'Copy'}
    />,
    <CancelButton
      onTouchTap={cancelCallback}
      label={'Cancel'}
    />
  ]

  return (
    <Dialog
      actions={actions}
      modal={false}
      onRequestClose={cancelCallback}
      open={open}
//     style={style}
      title={'Select a Project to copy this quote to:'}
    >
      <div class="dialog-body">
        <AutoCompleteSelect
          name="project"
          value={project}
          floatingLabelText="Project name"
//            selectedId={props.selectedId}
          dataStore={ProjectStore}
          autoCompleteFilter="campaignAutocomplete"
          autoCompleteAttribute='name'
//            autoCompleteAttribute="project"
          validatedOnChange={changeCallback}
          fullWidth
          required
          openOnFocus={true}
          errorMessage="You must select a project to copy to."
        />
      </div>
    </Dialog>
  )
}

/*
Props:
- deleteCallback: function(), called when delete is confirmed
- cancelCallback: function(), called on cancel
- open: Boolean - show dialog if true
- quote: the quote object to be deleted
*/
const QuoteDeleteConfirmation = ({
  deleteCallback,
  cancelCallback,
  open,
  quote
}) => {
  return (
    <ConfirmationDialog
      confirmLabel="Delete"
      confirmIcon={<ActionDelete />}
      confirmCallback={deleteCallback}
      cancelCallback={cancelCallback}
      title="Delete confirmation"
      open={open}
    >
      <p>
        Are you sure you want to delete the quote{' '}
        <strong>{quote.attributes.name}</strong>? There's no undo!
      </p>
    </ConfirmationDialog>
  )
}

/*
Props:
- fromState: String: original state
- toState: String: next state
- confirmCallback: function(), called when state change is confirmed
- cancelCallback: function(), called on cancel
- open: Boolean - show dialog if true
- quote: the quote object
*/
const QuoteStateChangeConfirmation = ({
  fromState,
  toState,
  confirmCallback,
  cancelCallback,
  open,
  quote
}) => (
  <ConfirmationDialog
    confirmCallback={confirmCallback}
    cancelCallback={cancelCallback}
    open={open}
    title="Quote state change confirmation"
  >
    <Fragment>
      <p>
        Are you sure you want to move the quote{' '}
        <strong>{quote.attributes.name}</strong> from{' '}
        <strong>{capitalize(fromState)}</strong> to{' '}
        <strong>{capitalize(toState)}</strong>?
      </p>

      {toState === 'pending' && (
        <p>
          Marking a quote as <strong>Pending</strong> means the client has
          received it and is currently reviewing it.
        </p>
      )}

      {toState === 'awarded' && (
        <p>
          Marking a quote as <strong>Awarded</strong> means the client has
          reviewed it and production can proceed.
        </p>
      )}

      {toState === 'actual' && (
        <Fragment>
          <p>
            Marking a quote as <strong>Actual</strong> indicates these are the
            actual values to be billed.
          </p>
          <p>
            <strong>
              <span style={{ color: red500 }}>IMPORTANT:</span> You can't
              backtrack from Actual!
            </strong>
          </p>
        </Fragment>
      )}
    </Fragment>
  </ConfirmationDialog>
)

/*
MAIN COMPONENT -----------------------------------------------------------------

NOTE: this component is meant to be used from within QuotesPager or similar

Props:
- project: a project object
- quoteId: Automatically selects a given quote (optional)
- readOnly: Boolean(false): self-explanatory
- selectCallback(quote): Callback function to be called when
                              a quote is selected
- refreshCallback(): Callback function to be called when the container
                     component needs to be refreshed. This will
                     usually be QuotesPager.
*/
class QuotesManager extends React.PureComponent {
  constructor(props) {
    super(props)

    const { project } = this.props

    this.state = {
      project: project,
      selectedQuote: null,
      showCopyDialog: false,
      copyProject: null,
      showDeleteConfirmation: false,
      showStateChangeConfirmation: false,
      fromState: '',
      toState: '',
      error: null,
      redirect: ''
    }
  }

  componentDidMount() {
    const { quoteId } = this.props
    if (quoteId) this._loadQuote(quoteId)
  }

  _refresh = () => {
    const { refreshCallback, selectCallback } = this.props

    this.setState(
      {
        showCopyDialog: false,
        showDeleteConfirmation: false,
        showStateChangeConfirmation: false,
        selectedQuote: null,
        error: null
      },
      () => {
        if (selectCallback) selectCallback(null)
        if (refreshCallback) refreshCallback()
      }
    )
  }

  _loadQuote = quoteId => {
    const { selectCallback } = this.props
    QuoteStore.find(quoteId)
      .then(result => {
        this.setState({ selectedQuote: result.data }, () => {
          if (selectCallback) selectCallback(result.data)
        })
      })
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _projectChangeCopy = (value, valid, attribute) =>
  {
    if (valid && (attribute === 'project'))
    {
      this.setState({ copyProject: value })
    }
  };

  _confirmCopy = row => {
    QuoteStore.find(row.id)
      .then(result =>
        this.setState({
          selectedQuote: result.data,
          showCopyDialog: true
        })
      )
      .catch(error =>
          this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _cancelCopy = () => this.setState({ showCopyDialog: false })

  _copyQuote = () => {
    const { selectedQuote, copyProject } = this.state

    QuoteStore
      .clone(selectedQuote.id)
      .then((result) => {
        QuoteStore
          .doCustomAction(`${result.data.id}/relationships/project`, 'PATCH', {data: { type: 'projects', id: copyProject } })
          .then(() => this.setState({ redirect: `/projects/${copyProject}`, showCopyDialog: false }))
          .catch(error => this.setState({ error: errors.unpack(error) }))
      })
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _confirmDelete = row => {
    QuoteStore.find(row.id)
      .then(result =>
        this.setState({
          selectedQuote: result.data,
          showDeleteConfirmation: true
        })
      )
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _cancelDelete = () => this.setState({ showDeleteConfirmation: false })

  _deleteQuote = () => {
    const { selectedQuote } = this.state

    // Take care of race condition
    if (!selectedQuote) {
      this.setState({ showDeleteConfirmation: false })
      return
    }

    QuoteStore.destroy(selectedQuote.id)
      .then(() => this._refresh())
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _printQuote = row => {
    const projectId = this.state.project.id
    const quoteId = row.id
    this.setState({
      redirect: `/projects/${projectId}/quotes/${quoteId}/print`
    })
  }

  _editQuote = row => {
    const projectId = this.state.project.id
    const quoteId = row.id
    this.setState({
      redirect: `/projects/${projectId}/quotes/${quoteId}/edit`
    })
  }

  _cloneQuote = row => {
    const quoteId = row.id
    QuoteStore.clone(quoteId)
      .then(() => this._refresh())
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _cloneActual = row => {
    const quoteId = row.id
    QuoteStore.clone(quoteId)
      .then(clonedQuote =>
        QuoteStore.doAction('actualize', clonedQuote.data.id, 'POST')
      )
      .then(() => this._refresh())
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _confirmStateChange = (row, toState) => {
    // Don't do anything if current and next state are the same
    if (row.state === toState) return

    QuoteStore.find(row.id)
      .then(result => {
        const selectedQuote = result.data
        const fromState = selectedQuote.attributes.state
        this.setState({
          showStateChangeConfirmation: true,
          selectedQuote,
          fromState,
          toState
        })
      })
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _changeQuoteState = () => {
    const { selectedQuote, toState } = this.state

    // Take care of race condition
    if (!selectedQuote) {
      this.setState({ showStateChangeConfirmation: false })
      return
    }

    // Call appropriate function if we're moving to 'actual'
    if (toState === 'actual') {
      this._actualize()
      return
    }

    // Change state
    QuoteStore.update(selectedQuote.id, { state: toState })
      .then(() => this._refresh())
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _actualize = () => {
    const { selectedQuote } = this.state

    // Create actual
    QuoteStore.doAction('actualize', selectedQuote.id, 'POST')
      .then(() => this._refresh())
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _cancelQuoteStateChange = () =>
    this.setState({
      showStateChangeConfirmation: false,
      fromState: '',
      toState: ''
    })

  render() {
    const { readOnly } = this.props

    const {
      project,
      selectedQuote,
      copyProject,
      showCopyDialog,
      showDeleteConfirmation,
      showStateChangeConfirmation,
      fromState,
      toState,
      error,
      redirect
    } = this.state

    // Just skip everything if we're redirecting
    if (redirect) return <Redirect push to={redirect} />

    const projectIsInactive = Boolean(
      project.attributes.state.match(/archived|dead/)
    )

    return (
      <section>
        <Row>
          <Col sm="12">
            <ListQuotes
              projectId={project.id}
              rowClickFn={this._loadQuote}
              rowDetailComponent={({ row }) => (
                <QuoteDetailsRow
                  quoteRow={row}
                  readOnly={projectIsInactive || readOnly}
                  editCallback={() => this._editQuote(row)}
                  cloneCallback={() => this._cloneQuote(row)}
                  cloneActualCallback={() => this._cloneActual(row)}
                  copyCallback={() => this._confirmCopy(row)}
                  deleteCallback={() => this._confirmDelete(row)}
                  printCallback={() => this._printQuote(row)}
                  draftCallback={() => this._confirmStateChange(row, 'draft')}
                  pendingCallback={() =>
                    this._confirmStateChange(row, 'pending')
                  }
                  awardedCallback={() =>
                    this._confirmStateChange(row, 'awarded')
                  }
                  actualCallback={() => this._confirmStateChange(row, 'actual')}
                />
              )}
              disableFilters
              disablePager
              disableSearch
              disableVisibility
            />
          </Col>
        </Row>

        {selectedQuote && (
          <QuoteDeleteConfirmation
            quote={selectedQuote}
            open={showDeleteConfirmation}
            deleteCallback={this._deleteQuote}
            cancelCallback={this._cancelDelete}
          />
        )}

        {selectedQuote && (
          <QuoteCopyDialog
            quote={selectedQuote}
            open={showCopyDialog}
            project={copyProject}
            changeCallback={this._projectChangeCopy}
            copyCallback={this._copyQuote}
            cancelCallback={this._cancelCopy}
          />
        )}

        {selectedQuote && (
          <QuoteStateChangeConfirmation
            quote={selectedQuote}
            open={showStateChangeConfirmation}
            fromState={fromState}
            toState={toState}
            confirmCallback={this._changeQuoteState}
            cancelCallback={this._cancelQuoteStateChange}
          />
        )}

        <ErrorDialog
          open={Boolean(error)}
          closeCallback={() => this.setState({ error: null })}
        >
          <p>
            <strong>Error:</strong> {error}
          </p>
        </ErrorDialog>
      </section>
    )
  }
}

export default QuotesManager



// WEBPACK FOOTER //
// ./src/components/Quotes/QuotesManager.jsx